/**
 * Multi-provider AI service for the web Shell.
 * Mirrors AIService.kt — same provider names, same error prefix format.
 *
 * Supported providers: "gemini" (default), "anthropic", "openai", "ollama".
 * For ollama the profile's `apiKey` field carries the base URL (e.g.
 * http://localhost:11434) — there is no real key.
 * All functions return a plain string: the AI reply, or an "ERROR: …" prefix on failure.
 */

export type AiProvider = 'gemini' | 'anthropic' | 'openai' | 'ollama';

/** Default base URL when an Ollama profile leaves the field blank. */
export const OLLAMA_DEFAULT_BASE_URL = 'http://localhost:11434';

/**
 * Appended to the reply when the provider stopped at its output-token cap.
 * Mirrors AIService.kt — callers (e.g. the Module Builder) detect this to
 * warn that generated code is incomplete instead of installing a broken file.
 */
export const TRUNCATION_MARKER = '<!--SHELL_TRUNCATED-->';

export interface AiCallParams {
  prompt: string;
  systemPrompt: string;
  apiKey: string;
  modelId: string;
  provider: AiProvider;
}

export async function callAI(params: AiCallParams): Promise<string> {
  const { prompt, systemPrompt, apiKey, modelId, provider } = params;
  if (provider === 'ollama') {
    return promptOllama(prompt, systemPrompt, apiKey || OLLAMA_DEFAULT_BASE_URL, modelId);
  }
  if (!apiKey.trim()) return 'ERROR: API key is missing.';
  switch (provider) {
    case 'anthropic': return promptClaude(prompt, systemPrompt, apiKey, modelId);
    case 'openai':    return promptOpenAI(prompt, systemPrompt, apiKey, modelId);
    default:          return promptGemini(prompt, systemPrompt, apiKey, modelId);
  }
}

// ─── Google Gemini ────────────────────────────────────────────────────────────

async function promptGemini(
  prompt: string, systemPrompt: string, apiKey: string, modelId: string,
): Promise<string> {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(modelId)}:generateContent?key=${encodeURIComponent(apiKey)}`;
  const supportsSystem = modelId.includes('gemini-1.5') || modelId.includes('gemini-2');
  const userContent = !supportsSystem && systemPrompt
    ? `System Context: ${systemPrompt}\n\nPrompt: ${prompt}`
    : prompt;

  const body: Record<string, unknown> = {
    contents: [{ role: 'user', parts: [{ text: userContent }] }],
  };
  if (supportsSystem && systemPrompt) {
    body['system_instruction'] = { parts: [{ text: systemPrompt }] };
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      return `ERROR [Gemini ${res.status}]: ${err}`;
    }
    const data = await res.json() as GeminiResponse;
    const candidate = data.candidates?.[0];
    const text = candidate?.content?.parts?.[0]?.text;
    if (text == null) return 'ERROR: Empty response from Gemini.';
    const truncated = candidate?.finishReason === 'MAX_TOKENS';
    return text.trim() + (truncated ? TRUNCATION_MARKER : '');
  } catch (e) {
    return `ERROR [Gemini]: ${(e as Error).message}`;
  }
}

// ─── Anthropic Claude ─────────────────────────────────────────────────────────

async function promptClaude(
  prompt: string, systemPrompt: string, apiKey: string, modelId: string,
): Promise<string> {
  const body: Record<string, unknown> = {
    model: modelId,
    // Claude 3-era models cap at 8192 output tokens; newer ones allow far
    // more — full HTML modules routinely exceed 8k. Matches AIService.kt.
    max_tokens: modelId.startsWith('claude-3') ? 8192 : 32000,
    messages: [{ role: 'user', content: prompt }],
  };
  if (systemPrompt) body['system'] = systemPrompt;

  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        // Required to allow direct browser-to-API calls:
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      return `ERROR [Claude ${res.status}]: ${err}`;
    }
    const data = await res.json() as ClaudeResponse;
    const text = data.content?.[0]?.text;
    if (text == null) return 'ERROR: Empty response from Claude.';
    const truncated = data.stop_reason === 'max_tokens';
    return text.trim() + (truncated ? TRUNCATION_MARKER : '');
  } catch (e) {
    return `ERROR [Claude]: ${(e as Error).message}`;
  }
}

// ─── OpenAI ChatGPT ───────────────────────────────────────────────────────────

async function promptOpenAI(
  prompt: string, systemPrompt: string, apiKey: string, modelId: string,
): Promise<string> {
  const messages: Array<{ role: string; content: string }> = [];
  if (systemPrompt) messages.push({ role: 'system', content: systemPrompt });
  messages.push({ role: 'user', content: prompt });

  try {
    const res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({ model: modelId, messages }),
    });
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      return `ERROR [OpenAI ${res.status}]: ${err}`;
    }
    const data = await res.json() as OpenAIResponse;
    const choice = data.choices?.[0];
    const text = choice?.message?.content;
    if (text == null) return 'ERROR: Empty response from OpenAI.';
    const truncated = choice?.finish_reason === 'length';
    return text.trim() + (truncated ? TRUNCATION_MARKER : '');
  } catch (e) {
    return `ERROR [OpenAI]: ${(e as Error).message}`;
  }
}

// ─── Ollama (local, OpenAI-compatible) ────────────────────────────────────────

function normalizeOllamaBaseUrl(raw: string): string {
  let url = raw.trim() || OLLAMA_DEFAULT_BASE_URL;
  if (!/^https?:\/\//i.test(url)) url = `http://${url}`;
  return url.replace(/\/+$/, '');
}

/**
 * Returns an actionable error string when the browser will refuse to reach the
 * given Ollama base URL, or null when the request is worth attempting.
 *
 * A secure public page (e.g. https://*.pages.dev) cannot reach a local address
 * like http://localhost:11434 — Chrome blocks it via Private Network Access /
 * mixed-content. The raw failure is a useless "Failed to fetch", so we detect
 * the situation up-front and explain the real fix.
 */
export function ollamaReachabilityError(base: string): string | null {
  if (typeof window === 'undefined') return null;
  const targetIsLocal = /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:|\/|$)/i.test(base);
  const pageHost = window.location.hostname;
  const pageIsRemoteHttps =
    window.location.protocol === 'https:' &&
    !/^(localhost|127\.0\.0\.1)$/i.test(pageHost);
  if (targetIsLocal && pageIsRemoteHttps) {
    return `ERROR [Ollama]: This page is served over HTTPS (${window.location.origin}), and ` +
           `browsers block a secure public site from reaching a local address like ${base}. ` +
           `To use Ollama: open the app locally at http://localhost, OR expose Ollama over HTTPS ` +
           `with a tunnel and use that URL, OR generate from the Android app instead.`;
  }
  return null;
}

async function promptOllama(
  prompt: string, systemPrompt: string, baseUrl: string, modelId: string,
): Promise<string> {
  const base = normalizeOllamaBaseUrl(baseUrl);
  // Guard against the classic mistake of saving the web app's own address as
  // the base URL (often browser autofill). Ollama never lives on this origin,
  // so the request would always fail with a confusing "Failed to fetch".
  if (typeof window !== 'undefined' && base.replace(/^https?:\/\//, '') === window.location.host) {
    return `ERROR [Ollama]: The base URL points at this web app (${base}), not the Ollama service. ` +
           `Edit the profile and set the Base URL to http://localhost:11434 (or your Ollama machine's address).`;
  }
  const ollamaBlocked = ollamaReachabilityError(base);
  if (ollamaBlocked) return ollamaBlocked;
  const messages: Array<{ role: string; content: string }> = [];
  if (systemPrompt) messages.push({ role: 'system', content: systemPrompt });
  messages.push({ role: 'user', content: prompt });
  const url = `${base}/v1/chat/completions`;

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: modelId, messages, stream: false }),
    });
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      return `ERROR [Ollama ${res.status}]: ${err}`;
    }
    const data = await res.json() as OpenAIResponse;
    const choice = data.choices?.[0];
    const text = choice?.message?.content;
    if (text == null) return 'ERROR: Empty response from Ollama.';
    const truncated = choice?.finish_reason === 'length';
    return text.trim() + (truncated ? TRUNCATION_MARKER : '');
  } catch (e) {
    return `ERROR [Ollama]: ${(e as Error).message} — is the Ollama service running at ${base}?`;
  }
}

// ─── Response shapes (minimal) ────────────────────────────────────────────────

interface GeminiResponse {
  candidates?: Array<{ content?: { parts?: Array<{ text?: string }> }; finishReason?: string }>;
}
interface ClaudeResponse {
  content?: Array<{ text?: string }>;
  stop_reason?: string;
}
interface OpenAIResponse {
  choices?: Array<{ message?: { content?: string }; finish_reason?: string }>;
}
